import type { ReactNode, RefObject } from "react";
import { cn } from "nazare-ui/utils";
import { PageSection, type PageSectionProps } from "nazare-ui/components/page-section";

import { ScrollShadow, TABLE_HEADER_HEIGHT } from "./scroll-shadow";
import { useScrollEdges } from "./use-scroll-edges";
import styles from "./table-section.module.css";

export type TableSectionProps = Omit<PageSectionProps, "children" | "padded"> & {
  children: ReactNode;
  tableWrapClassName?: string;
  tableWrapRef?: RefObject<HTMLDivElement | null>;
  tableWrapAriaLabel?: string;
  tableWrapTabIndex?: number;
  shadowTopOffset?: number;
  shadowZIndex?: number;
};

/** The shared card and scrolling shell used by dashboard tables. */
export function TableSection({
  children,
  className,
  tableWrapClassName,
  tableWrapRef,
  tableWrapAriaLabel,
  tableWrapTabIndex,
  shadowTopOffset = TABLE_HEADER_HEIGHT,
  shadowZIndex,
  ...sectionProps
}: TableSectionProps) {
  const { ref, atTop, atBottom } = useScrollEdges<HTMLDivElement>(tableWrapRef);

  return (
    <PageSection {...sectionProps} className={cn(styles.card, className)}>
      <div className={styles.scrollShadowWrapper}>
        <div
          ref={ref}
          className={cn(styles.tableWrap, tableWrapClassName)}
          role={tableWrapAriaLabel ? "region" : undefined}
          aria-label={tableWrapAriaLabel}
          tabIndex={tableWrapTabIndex}
        >
          {children}
        </div>
        <ScrollShadow
          atTop={atTop}
          atBottom={atBottom}
          topOffset={shadowTopOffset}
          zIndex={shadowZIndex}
        />
      </div>
    </PageSection>
  );
}
